import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react'
import { Outlet } from 'react-router-dom'
import { Sidebar } from './Sidebar'
import { Topbar } from './Topbar'
import { Toaster } from '@/components/common/Toaster'
import { FilterNotice } from '@/components/common/FilterNotice'
import { useToast } from '@/hooks/useToast'
import { useAuth } from '@/hooks/useAuth'
import { usePreferences } from '@/lib/preferences'
import { MUTATION_ERROR_EVENT, type MutationErrorDetail } from '@/api/queryClient'
import { SkipLink, OfflineBanner, BackToTop, PageTitleTracker } from './ShellUtilities'
import { IdleTimeout } from './IdleTimeout'
import { ShortcutsDialog, useGlobalShortcuts } from './KeyboardShortcuts'
import { WelcomeTour, WhatsNewDialog } from './WelcomeAndNews'
import { BrandingApplier } from './BrandingApplier'
import { UnsavedChangesGuard } from '@/hooks/useUnsavedChanges'
import { MigrationBanner } from './MigrationBanner'
import { SchedulerHealthBanner } from '@/components/common/SchedulerHealthBanner'
import { ForcePasswordChangeBanner } from '@/components/common/ForcePasswordChangeBanner'
import { SupportAccessBanner } from '@/components/security/SupportAccess'
import { isNativeApp } from '@/mobile/platform'
import { AssistantLauncher } from '@/features/assistant/AssistantLauncher'

// Android app only (tab bar, back button, push, pull-to-refresh). Never loaded on the web.
const MobileShell = lazy(() => import('@/mobile/MobileShell'))
const NATIVE = isNativeApp()

export function AppShell() {
  const toast = useToast()
  const { user } = useAuth()
  const { prefs } = usePreferences()
  const mainRef = useRef<HTMLElement>(null)
  const [shortcutsOpen, setShortcutsOpen] = useState(false)
  const [welcomeOpen, setWelcomeOpen] = useState(false)
  const [newsOpen, setNewsOpen] = useState(false)
  const openShortcuts = useCallback(() => setShortcutsOpen(true), [])
  useGlobalShortcuts(openShortcuts)

  // Surface mutation failures that the page itself does not handle.
  useEffect(() => {
    const onErr = (e: Event) => {
      const { message, title } = (e as CustomEvent<MutationErrorDetail>).detail
      toast.error(message, { title, duration: 10000 })
    }
    window.addEventListener(MUTATION_ERROR_EVENT, onErr)
    return () => window.removeEventListener(MUTATION_ERROR_EVENT, onErr)
  }, [toast])

  // First sign-in: a short role-aware tour, once per person (server-side preference). Not shown
  // to browser automation (navigator.webdriver), so scripted test runs are never blocked by it.
  useEffect(() => {
    if (user && prefs.welcome_done === false && !navigator.webdriver) {
      const t = window.setTimeout(() => setWelcomeOpen(true), 600)
      return () => window.clearTimeout(t)
    }
  }, [user, prefs.welcome_done])

  return (
    <div className="flex h-dvh w-full overflow-hidden bg-background">
      <SkipLink />
      <BrandingApplier />
      <Sidebar />
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <Topbar onShortcuts={openShortcuts} onWelcome={() => setWelcomeOpen(true)} onWhatsNew={() => setNewsOpen(true)} />
        <OfflineBanner />
        <MigrationBanner />
        <SupportAccessBanner />
        <SchedulerHealthBanner />
        <ForcePasswordChangeBanner />
        <main id="main" ref={mainRef} tabIndex={-1} className="flex-1 overflow-y-auto outline-none">
          {/* Capped reading width on very wide screens so tables and forms stay scannable;
              the gutter tightens on small screens (spec §3.5). The 128px bottom pad lets the
              last row scroll clear of the floating Back-to-top button. */}
          <div className="mx-auto w-full max-w-[1600px] px-4 pb-32 pt-5 sm:px-6 sm:pt-6 lg:px-8 lg:pt-7">
            {/* In-flow banner (never an overlay) for filters the API could not apply — D-002. */}
            <FilterNotice />
            <Outlet />
          </div>
        </main>
        <BackToTop scrollRef={mainRef} />
      </div>
      <PageTitleTracker mainRef={mainRef} />
      <IdleTimeout />
      <UnsavedChangesGuard />
      <ShortcutsDialog open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
      <WelcomeTour open={welcomeOpen} onClose={() => setWelcomeOpen(false)} />
      <WhatsNewDialog open={newsOpen} onClose={() => setNewsOpen(false)} />
      <AssistantLauncher />
      <Toaster />
      {NATIVE && <Suspense fallback={null}><MobileShell mainRef={mainRef} /></Suspense>}
    </div>
  )
}
